// src/pages/UserPage.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    getUserFantasyTeams,
    getPlayersForTeams,
    resyncFantasyTeam,
    getUserDashboardBulk,
} from '../api/fantasyteams';
import { fetchSnippetsByPlayer } from '../api/snippets';
import {
    PlusIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { XMarkIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';

import YouTubePlayer from '../components/YouTubePlayer';
import UserProfileCard from '../components/UserProfileCard'; // Import the new component
import PlayerCard from '../components/PlayerCard'; // Import the new component
import TeamFilterCard from '../components/TeamFilterCard'; // Import the new component
import OverallStatsCard from '../components/OverallStatsCard'; // Import the new component
import DesktopPlayerTable from '../components/DesktopPlayerTable'; // Import the new component

// --- Helper Components ---
const LoadingSpinner = ({ size = 'md' }) => <span className={`loading loading-spinner loading-${size} text-primary`}></span>;
const ErrorMessage = ({ message }) => (
    <div role="alert" className="alert alert-error my-4 shadow-lg">
        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        <span>Error! {message || 'An unexpected error occurred.'}</span>
    </div>
);
const MOBILE_BREAKPOINT = 768;

// Default state for aggregated stats
const defaultAggregatedStats = {
    total_snippets: 0,
    positive_count: 0,
    negative_count: 0,
    net_score: 0,
    classification_counts: {}
};

const MAX_AVATAR_SIZE_MB = 2; // Max avatar size in MB
const MAX_AVATAR_SIZE_BYTES = MAX_AVATAR_SIZE_MB * 1024 * 1024;

// --- Main UserPage Component ---
function UserPage() {
    const {
        user,
        loading: authLoading,
        updateProfileData,
        uploadAvatar,
        fetchUserProfile
    } = useAuth();

    // --- State ---
    const [profile, setProfile] = useState(null); // Local state for profile data
    const [isLoadingProfile, setIsLoadingProfile] = useState(true); // Loading state for profile data

    const [userTeams, setUserTeams] = useState([]);
    const [selectedTeamIds, setSelectedTeamIds] = useState(new Set());
    
    // NEW: State for client-side filtering
    const [allPlayers, setAllPlayers] = useState([]); // Store all players for all teams
    const [playersTeamMap, setPlayersTeamMap] = useState({}); // Map player ID to team IDs
    
    // NEW: Snippet caching for client-side filtering
    const [allPlayerSnippets, setAllPlayerSnippets] = useState({}); // Cache all snippets for all players
    const [allSnippetsLoaded, setAllSnippetsLoaded] = useState(false); // Track if snippets are loaded
    
    const [displayedPlayers, setDisplayedPlayers] = useState([]);
    const [playerSnippets, setPlayerSnippets] = useState({}); // Filtered snippets for displayed players
    const [allClassifications, setAllClassifications] = useState([]);
    const [selectedDays, setSelectedDays] = useState(7);
    const [isLoadingTeams, setIsLoadingTeams] = useState(true);
    const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
    const [isLoadingSnippetsAndStats, setIsLoadingSnippetsAndStats] = useState(false);
    const [error, setError] = useState(null);
    const [expandedPlayerId, setExpandedPlayerId] = useState(null);

    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [editFirstName, setEditFirstName] = useState('');
    const [editLastName, setEditLastName] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState('');
    const [profileUpdateError, setProfileUpdateError] = useState('');
    const [profileUpdateSuccess, setProfileUpdateSuccess] = useState('');
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

    const [selectedClassifications, setSelectedClassifications] = useState(new Set()); // Changed for multi-select

    // --- Player State ---
    const [selectedSnippet, setSelectedSnippet] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPlayerMinimized, setIsPlayerMinimized] = useState(false);
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false
    );
    const [isPlayerSticky, setIsPlayerSticky] = useState(false); // State for sticky logic

    // Refs for sticky calculation
    const asideRef = useRef(null);
    const playerRef = useRef(null); // Ref for the player container itself
    const filterCardRef = useRef(null);

    const [teamToResync, setTeamToResync] = useState(null);
    const [isResyncing, setIsResyncing] = useState(false);

    // --- ADD BACK Carousel State for Mobile ---
    const [currentMobileSlideIndex, setCurrentMobileSlideIndex] = useState(0);
    const [touchStartX, setTouchStartX] = useState(null);
    const [touchEndX, setTouchEndX] = useState(null);
    const [isSwiping, setIsSwiping] = useState(false);
    const carouselRef = useRef(null); // Ref for the carousel container

    const navigate = useNavigate();
    const currentUserId = user?.id;

    // Effect to show/hide DaisyUI modal programmatically - REMOVE THIS
    // useEffect(() => {
    //     if (!removeTeamModalRef.current) return;
    //     if (isRemoveTeamModalOpen) {
    //         removeTeamModalRef.current.showModal();
    //     } else {
    //         if (removeTeamModalRef.current.open) {
    //              removeTeamModalRef.current.close();
    //         }
    //     }
    // }, [isRemoveTeamModalOpen]);

    // Effect to fetch user profile data
    useEffect(() => {
        if (currentUserId && fetchUserProfile) {
            setIsLoadingProfile(true);
            fetchUserProfile()
                .then(userProfile => {
                    setProfile(userProfile);
                    // Initialize avatar preview with fetched URL if not editing OR if editing but no new file selected yet
                    if (userProfile?.avatar_url && (!isEditingProfile || !selectedFile)) {
                        setAvatarPreview(userProfile.avatar_url);
                    }
                })
                .catch(err => {
                    console.error("UserPage: Failed to fetch profile:", err);
                    setError(prevError => prevError || "Could not load your profile details.");
                })
                .finally(() => {
                    setIsLoadingProfile(false);
                });
        } else if (!currentUserId && !authLoading) { // If no user and auth is done loading
            setProfile(null);
            setIsLoadingProfile(false);
        }
    }, [currentUserId, fetchUserProfile, authLoading, isEditingProfile, selectedFile]); // Added dependencies

    // Populate edit form when entering edit mode or profile (local state) loads
    useEffect(() => {
        if (isEditingProfile) {
            if (profile) { // Use local profile state
                setEditFirstName(profile.first_name || '');
                setEditLastName(profile.last_name || '');
                // Set avatar preview: if a file is already selected for upload, keep its preview, otherwise use current profile avatar.
                if (!selectedFile) {
                    setAvatarPreview(profile.avatar_url || '');
                }
            } else {
                 // If profile is null (e.g. still loading or error), clear edit fields
                setEditFirstName('');
                setEditLastName('');
                if (!selectedFile) {
                    setAvatarPreview('');
                }
            }
            // setSelectedFile(null); // Reset selected file when starting edit mode if you always want a fresh choice
            setProfileUpdateError('');
            setProfileUpdateSuccess('');
        }
    }, [profile, isEditingProfile, selectedFile]); // Added selectedFile dependency

    // Effect for generating local preview from selectedFile
    useEffect(() => {
        if (!selectedFile) {
            // If a file is deselected, and we are in edit mode,
            // revert preview to the current profile's avatar (if profile exists)
            if (isEditingProfile && profile) {
                setAvatarPreview(profile.avatar_url || '');
            }
            return;
        }
        const objectUrl = URL.createObjectURL(selectedFile);
        setAvatarPreview(objectUrl);

        // Cleanup
        return () => URL.revokeObjectURL(objectUrl);
    }, [selectedFile, isEditingProfile, profile]);

    // Mobile Viewport Detection Effect - Update for carousel
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const resizeCallback = () => {
            const newIsMobile = window.innerWidth < MOBILE_BREAKPOINT;
            if (newIsMobile !== isMobile) {
                // If switching to desktop with a snippet selected, stop the player
                if (!newIsMobile && selectedSnippet) {
                    handleStopPlayer();
                }
                // Always update isMobile state
                setIsMobile(newIsMobile);
                // Optionally reset minimize state and carousel index when switching modes
                setIsPlayerMinimized(false);
                if (newIsMobile) {
                    setCurrentMobileSlideIndex(0); // Reset to first slide on mobile
                }
            }
        };

        window.addEventListener('resize', resizeCallback);
        // Initial check in case the initial state was wrong
        resizeCallback();

        return () => window.removeEventListener('resize', resizeCallback);
        // Dependencies: isMobile to check if state needs update, selectedSnippet & handleStopPlayer for M->D stop logic
    }, [isMobile, selectedSnippet]); // handleStopPlayer is memoized by useCallback or stable if defined outside render

    // --- Sticky Player Scroll Logic (Desktop) ---
    useEffect(() => {
        if (isMobile || typeof window === 'undefined') {
            setIsPlayerSticky(false); // Ensure not sticky on mobile or SSR
            return;
        }

        const headerOffset = 80; // Approx height of fixed header + desired gap (adjust as needed)
        let isThrottled = false;

        const handleScroll = () => {
            if (isThrottled || !asideRef.current || !filterCardRef.current) return;

            isThrottled = true;
            setTimeout(() => {
                isThrottled = false;
            }, 100); // Simple throttle: check every 100ms

            const filterCardBottom = filterCardRef.current.getBoundingClientRect().bottom;
            const asideTop = asideRef.current.getBoundingClientRect().top;

            // Check if the bottom of the filter card is above the sticky position
            // and the aside container itself is still partially visible from the top
            if (filterCardBottom < headerOffset && asideTop < headerOffset) {
                if (!isPlayerSticky) setIsPlayerSticky(true);
            } else {
                if (isPlayerSticky) setIsPlayerSticky(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        // Initial check in case the page loads scrolled
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };

    }, [isMobile, isPlayerSticky]); // Re-run if mobile state changes or sticky state is updated externally

    // --- Memoized Callbacks for Data Fetching ---
    const fetchUserTeamsAndPlayers = useCallback(async () => {
        setIsLoadingTeams(true);
        setIsLoadingPlayers(true);
        setIsLoadingSnippetsAndStats(true);
        setError(null);
        setPlayerSnippets({});
        setAllClassifications([]);
        setExpandedPlayerId(null);
        setAllSnippetsLoaded(false);
        
        try {
            // Use the optimized bulk endpoint
            const bulkData = await getUserDashboardBulk(selectedDays === 0 ? 0 : selectedDays);
            
            // Transform the bulk response to match our existing state structure
            const teams = bulkData.teams || [];
            const players = bulkData.players || [];
            const snippetsData = bulkData.snippets || {};
            const playerTeamMapping = bulkData.player_team_mapping || {};
            
            setUserTeams(teams);
            setAllPlayers(players);
            setPlayersTeamMap(playerTeamMapping);
            setAllPlayerSnippets(snippetsData);
            setAllSnippetsLoaded(true);
            
            // Collect all classifications from snippets
            const classificationsSet = new Set();
            Object.values(snippetsData).forEach(playerSnippets => {
                playerSnippets.forEach(snippet => {
                    const classification = snippet.classification?.trim() || "Unclassified";
                    if (classification) classificationsSet.add(classification);
                });
            });
            
            // Set sorted classifications
            const sortedClassifications = Array.from(classificationsSet).sort((a, b) => {
                if (a === "Other" || a === "Unclassified") return 1;
                if (b === "Other" || b === "Unclassified") return -1;
                return a.localeCompare(b);
            });
            setAllClassifications(sortedClassifications);
            
            // Default to all teams selected when teams are loaded
            if (teams.length > 0) {
                setSelectedTeamIds(new Set(teams.map(t => t.id)));
            } else {
                setSelectedTeamIds(new Set());
            }
            
        } catch (err) {
            console.error("Error fetching user teams and players:", err);
            setError("Could not load your fantasy teams and players. Please try again later.");
            setUserTeams([]);
            setAllPlayers([]);
            setPlayersTeamMap({});
            setSelectedTeamIds(new Set());
            setAllPlayerSnippets({});
        } finally {
            setIsLoadingTeams(false);
            setIsLoadingPlayers(false);
            setIsLoadingSnippetsAndStats(false);
        }
    }, [selectedDays]);

    // NEW: Function to refetch all snippets for new time period (simplified since bulk API handles this)
    const refetchAllSnippets = useCallback(async () => {
        if (allPlayers.length === 0) return;
        
        setIsLoadingSnippetsAndStats(true);
        setAllSnippetsLoaded(false);
        
        try {
            // Use the same bulk endpoint but only update snippets
            const bulkData = await getUserDashboardBulk(selectedDays === 0 ? 0 : selectedDays);
            const snippetsData = bulkData.snippets || {};
            
            setAllPlayerSnippets(snippetsData);
            setAllSnippetsLoaded(true);
            
            // Update classifications from new snippets
            const classificationsSet = new Set();
            Object.values(snippetsData).forEach(playerSnippets => {
                playerSnippets.forEach(snippet => {
                    const classification = snippet.classification?.trim() || "Unclassified";
                    if (classification) classificationsSet.add(classification);
                });
            });
            
            const sortedClassifications = Array.from(classificationsSet).sort((a, b) => {
                if (a === "Other" || a === "Unclassified") return 1;
                if (b === "Other" || b === "Unclassified") return -1;
                return a.localeCompare(b);
            });
            setAllClassifications(sortedClassifications);
        } catch (err) {
            console.error("Error refetching snippets:", err);
            setError("Could not refresh snippet data for the selected time period.");
        } finally {
            setIsLoadingSnippetsAndStats(false);
        }
    }, [allPlayers, selectedDays]);

    // NEW: Client-side snippet filtering function
    const filterSnippetsForDisplayedPlayers = useCallback(() => {
        if (!allSnippetsLoaded || displayedPlayers.length === 0) {
            setPlayerSnippets({});
            return;
        }

        const filteredSnippets = {};
        displayedPlayers.forEach(player => {
            filteredSnippets[player.id] = allPlayerSnippets[player.id] || [];
        });
        
        setPlayerSnippets(filteredSnippets);
    }, [allPlayerSnippets, allSnippetsLoaded, displayedPlayers]);

    const { overallStats, perPlayerStats } = useMemo(() => {
        const newOverallStats = { ...defaultAggregatedStats, classification_counts: {} };
        const newPerPlayerStats = {};
        
        // Calculate unfiltered classification counts for the badges first
        const unfilteredClassificationCounts = {};
        displayedPlayers.forEach(player => {
            const snippets = playerSnippets[player.id] || [];
            snippets.forEach(snippet => {
                const classification = snippet.classification?.trim() || "Unclassified";
                unfilteredClassificationCounts[classification] = (unfilteredClassificationCounts[classification] || 0) + 1;
            });
        });
        // This is what the filter badges will display as their counts
        newOverallStats.classification_counts = unfilteredClassificationCounts; 

        // Now, proceed with filtering snippets based on selectedClassifications for actual stat calculation
        displayedPlayers.forEach(player => {
            const snippets = playerSnippets[player.id] || [];
            const playerStatClassifications = {};
            allClassifications.forEach(cls => { playerStatClassifications[cls] = 0; });

            let positiveForPlayer = 0;
            let negativeForPlayer = 0;
            let countForPlayer = 0;

            snippets.forEach(snippet => {
                const sentiment = snippet.sentiment?.trim().toLowerCase();
                const classification = snippet.classification?.trim() || "Unclassified";

                // Apply multi-select classification filter
                if (selectedClassifications.size > 0 && !selectedClassifications.has(classification)) {
                    return; // Skip this snippet if it doesn't match any of the selected filters
                }

                countForPlayer++;

                if (sentiment === 'positive') positiveForPlayer++;
                else if (sentiment === 'negative') negativeForPlayer++;

                if (playerStatClassifications.hasOwnProperty(classification)) {
                    playerStatClassifications[classification]++;
                } else if (classification) {
                    playerStatClassifications[classification] = 1;
                }
                // No need to update overallClassificationsCount here anymore, it's pre-calculated and unfiltered
            });

            newPerPlayerStats[player.id] = {
                total: countForPlayer,
                positive: positiveForPlayer,
                negative: negativeForPlayer,
                classifications: playerStatClassifications
            };
        });

        newOverallStats.total_snippets = displayedPlayers.reduce((sum, p) => sum + (newPerPlayerStats[p.id]?.total || 0), 0);
        newOverallStats.positive_count = displayedPlayers.reduce((sum, p) => sum + (newPerPlayerStats[p.id]?.positive || 0), 0);
        newOverallStats.negative_count = displayedPlayers.reduce((sum, p) => sum + (newPerPlayerStats[p.id]?.negative || 0), 0);
        newOverallStats.net_score = newOverallStats.positive_count - newOverallStats.negative_count;
        // newOverallStats.classification_counts is already set to the unfiltered counts for badge display

        return { overallStats: newOverallStats, perPlayerStats: newPerPlayerStats };
    }, [playerSnippets, displayedPlayers, allClassifications, selectedClassifications]);

    // --- Data Fetching Effects ---
    // Initial fetch for teams and all players
    useEffect(() => {
        if (currentUserId) {
            fetchUserTeamsAndPlayers();
        }
    }, [currentUserId, fetchUserTeamsAndPlayers]); // fetchUserTeamsAndPlayers is memoized

    // NEW: Client-side filtering effect - Filter players when selectedTeamIds change
    useEffect(() => {
        if (selectedTeamIds.size === 0) {
            setDisplayedPlayers([]);
            return;
        }

        // Filter allPlayers based on selected team IDs
        const filteredPlayers = allPlayers.filter(player => {
            const playerTeamIds = playersTeamMap[player.id] || [];
            return playerTeamIds.some(teamId => selectedTeamIds.has(teamId));
        });

        setDisplayedPlayers(filteredPlayers);
    }, [allPlayers, playersTeamMap, selectedTeamIds]);

    // Filter snippets client-side when displayedPlayers change
    useEffect(() => {
        filterSnippetsForDisplayedPlayers();
    }, [filterSnippetsForDisplayedPlayers]);

    // Handle selectedDays change - need to refetch all snippets for new time period
    useEffect(() => {
        if (allPlayers.length > 0 && allSnippetsLoaded) {
            // Only refetch snippets if we have players and have loaded snippets before
            refetchAllSnippets();
        }
    }, [selectedDays]); // Only depend on selectedDays to avoid infinite loops

    // --- Handlers ---
    const handleTeamSelectionChange = (teamId) => {
        setSelectedTeamIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(teamId)) {
                newSet.delete(teamId);
            } else {
                newSet.add(teamId);
            }
            return newSet;
        });
    };
    const handleSelectAllTeams = () => setSelectedTeamIds(new Set(userTeams.map(t => t.id)));
    const handleSelectNoneTeams = () => setSelectedTeamIds(new Set());
    const handlePlayerClick = (playerId) => navigate(`/player/${playerId}`);
    const handleDaysButtonClick = (daysValue) => { setSelectedDays(daysValue); };
    const toggleSnippetsExpansion = (playerId) => {
        setExpandedPlayerId(prevId => (prevId === playerId ? null : playerId));
    };

    const handleClassificationFilterClick = (classification) => {
        setSelectedClassifications(prev => {
            const next = new Set(prev);
            if (next.has(classification)) {
                next.delete(classification);
            } else {
                next.add(classification);
            }
            return next;
        });
    };

    // --- Player Handlers ---
    const handlePlaySnippet = (snippet) => {
        setSelectedSnippet(snippet);
        setIsPlaying(true);
        setIsPlayerMinimized(false); // Expand player when a new snippet is selected
        // Optionally scroll player into view on mobile
        // if (isMobile) { document.getElementById('mobile-sticky-player-container')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
    };

    const handleStopPlayer = () => {
        setSelectedSnippet(null);
        setIsPlaying(false);
        setIsPlayerMinimized(false);
    };

    const togglePlayerMinimize = () => {
        setIsPlayerMinimized(prev => !prev);
    };

    const displayName = useMemo(() => {
        if (authLoading || isLoadingProfile) return 'Loading...'; // Check both auth and profile loading
        if (profile) { // Use local profile state
            if (profile.first_name && profile.last_name) return `${profile.first_name} ${profile.last_name}`;
            if (profile.first_name) return profile.first_name;
        }
        if (user) return user.email; // Fallback to email if profile names are not available
        return 'User';
    }, [profile, user, authLoading, isLoadingProfile]); // Updated dependencies

    const displayInitial = useMemo(() => {
        if (authLoading || isLoadingProfile) return '';
        if (profile && profile.first_name) return profile.first_name.charAt(0).toUpperCase();
        if (user && user.email) return user.email.charAt(0).toUpperCase();
        return 'U';
    }, [profile, user, authLoading, isLoadingProfile]); // Updated dependencies

    const handleEditProfileToggle = () => {
        if (!isEditingProfile) { // Entering edit mode
            if (profile) { // Use local profile state
                setEditFirstName(profile.first_name || '');
                setEditLastName(profile.last_name || '');
                setAvatarPreview(profile.avatar_url || ''); // Set current avatar as initial preview
            } else {
                // Handle case where profile might still be null
                setEditFirstName('');
                setEditLastName('');
                setAvatarPreview('');
            }
            setSelectedFile(null); // Clear any previously selected file
        } else { // Cancelling edit mode
            setSelectedFile(null); // Clear selected file
            // Revert avatar preview to the original profile avatar if profile exists
            if (profile) {
                setAvatarPreview(profile.avatar_url || '');
            } else {
                setAvatarPreview('');
            }
        }
        setIsEditingProfile(!isEditingProfile);
        setProfileUpdateError('');
        setProfileUpdateSuccess('');
    };

    const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        setProfileUpdateError('');
        setProfileUpdateSuccess('');

        if (!file) {
            setSelectedFile(null);
            // Revert preview to currently saved avatar (if profile exists) or clear if no profile
            setAvatarPreview(profile?.avatar_url || '');
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            setProfileUpdateError('Invalid file type. Please select a JPG, PNG, or GIF.');
            setSelectedFile(null);
            event.target.value = ''; // Clear the file input
            setAvatarPreview(profile?.avatar_url || ''); // Revert preview
            return;
        }

        if (file.size > MAX_AVATAR_SIZE_BYTES) {
            setProfileUpdateError(`File is too large. Max size is ${MAX_AVATAR_SIZE_MB}MB.`);
            setSelectedFile(null);
            event.target.value = ''; // Clear the file input
            setAvatarPreview(profile?.avatar_url || ''); // Revert preview
            return;
        }
        setSelectedFile(file);
        // Preview will be handled by the useEffect watching 'selectedFile'
    };

    const handleProfileFormSubmit = async (e) => {
        e.preventDefault();
        if (!updateProfileData) {
            setProfileUpdateError("Profile update function is not available.");
            return;
        }
        if (selectedFile && !uploadAvatar) {
            setProfileUpdateError("Avatar upload function is not available.");
            return;
        }

        setProfileUpdateError('');
        setProfileUpdateSuccess('');
        setIsUpdatingProfile(true);

        let newAvatarUrlToSave = profile?.avatar_url; // Start with current (or null)

        try {
            if (selectedFile) {
                newAvatarUrlToSave = await uploadAvatar(selectedFile);
            }

            const updates = {
                first_name: editFirstName.trim(),
                last_name: editLastName.trim(),
            };

            // Only include avatar_url in updates if it has changed
            if (newAvatarUrlToSave !== profile?.avatar_url) {
                 updates.avatar_url = newAvatarUrlToSave || null;
            }


            await updateProfileData(updates);

            // Re-fetch the profile to get the latest data including the new avatar_url from the DB
            if (fetchUserProfile) {
                const updatedProfileData = await fetchUserProfile();
                setProfile(updatedProfileData); // Update local profile state
                if (updatedProfileData?.avatar_url) {
                    setAvatarPreview(updatedProfileData.avatar_url); // Ensure preview uses the new DB URL
                } else if (!selectedFile) { // If avatar was removed
                    setAvatarPreview('');
                }
            }

            setProfileUpdateSuccess('Profile updated successfully!');
            setIsEditingProfile(false);
            setSelectedFile(null); // Clear selected file after successful save
        } catch (error) {
            console.error("Error updating profile:", error);
            setProfileUpdateError(error.message || "Failed to update profile.");
            // On error, revert avatar preview to the last known good one (if a file was selected)
            if (selectedFile && profile) {
                setAvatarPreview(profile.avatar_url || '');
            }
        } finally {
            setIsUpdatingProfile(false);
        }
    };

    const isAllTeamsSelected = useMemo(() => userTeams.length > 0 && selectedTeamIds.size === userTeams.length, [userTeams, selectedTeamIds]);

    // --- Event Handlers ---
    // REMOVE handleOpenRemoveTeamModal, handleCloseRemoveTeamModal, handleTeamSelectionForDeletion, handleConfirmDeleteTeams

    // NEW: Callback for when teams are successfully deleted in TeamFilterCard
    const handleTeamsDeletedSuccessfully = useCallback(() => {
        toast.success('Dashboard refreshing after team deletion...'); // Or use a page level success message if preferred
        fetchUserTeamsAndPlayers(); // Refresh the primary list of teams and players
        setSelectedTeamIds(new Set()); // Clear current team selections
        setDisplayedPlayers([]);      // Clear displayed players
        setPlayerSnippets({});        // Clear their snippets
        setExpandedPlayerId(null);    // Close any expanded player
        // Overall stats and classifications will update automatically due to dependencies on displayedPlayers/playerSnippets
    }, [fetchUserTeamsAndPlayers]); // fetchUserTeamsAndPlayers is memoized

    const handleResyncTeam = async (teamIdToResync) => {
        if (isResyncing && teamToResync === teamIdToResync) return;
        setTeamToResync(teamIdToResync);
        setIsResyncing(true);
        const toastId = toast.loading(`Re-syncing team...`);
        try {
            const updatedTeam = await resyncFantasyTeam(teamIdToResync);
            toast.success(`Team "${updatedTeam.team_name || 'Team'}" re-synced successfully!`, { id: toastId });
            setUserTeams(prevTeams =>
                prevTeams.map(team =>
                    team.id === teamIdToResync ? { ...team, ...updatedTeam } : team
                )
            );
            if (selectedTeamIds.has(teamIdToResync)) {
                // Re-fetch players for the specific team to update any changes
                try {
                    const updatedPlayers = await getPlayersForTeams([teamIdToResync]);
                    if (Array.isArray(updatedPlayers)) {
                        // Update the players in our cache
                        setAllPlayers(prevPlayers => {
                            const filteredPrevPlayers = prevPlayers.filter(p => {
                                const playerTeamIds = playersTeamMap[p.id] || [];
                                return !playerTeamIds.includes(teamIdToResync);
                            });
                            return [...filteredPrevPlayers, ...updatedPlayers];
                        });
                        
                        // Update the team mapping for these players
                        setPlayersTeamMap(prevMapping => {
                            const newMapping = { ...prevMapping };
                            updatedPlayers.forEach(player => {
                                if (!newMapping[player.id]) {
                                    newMapping[player.id] = [];
                                }
                                if (!newMapping[player.id].includes(teamIdToResync)) {
                                    newMapping[player.id].push(teamIdToResync);
                                }
                            });
                            return newMapping;
                        });
                    }
                } catch (playerErr) {
                    console.error(`Failed to refresh players for resynced team ${teamIdToResync}:`, playerErr);
                    // Non-critical error, don't show to user
                }
            }
        } catch (error) {
            console.error("Failed to re-sync team:", error);
            toast.error(`Error re-syncing team: ${error.message || 'Unknown error'}`, { id: toastId });
        } finally {
            setIsResyncing(false);
            setTeamToResync(null);
        }
    };

    // --- NEW: Carousel Handlers ---
    const MIN_SWIPE_DISTANCE = 50; // Minimum distance in pixels for a swipe

    const handleNextSlide = () => {
        setCurrentMobileSlideIndex(prev => Math.min(prev + 1, 2)); // Assuming 3 slides
    };

    const handlePrevSlide = () => {
        setCurrentMobileSlideIndex(prev => Math.max(prev - 1, 0));
    };

    const handleTouchStart = (e) => {
        if (e.touches.length !== 1) {
            setIsSwiping(false);
            return;
        }
        setTouchEndX(null);
        setTouchStartX(e.touches[0].clientX);
        setIsSwiping(true);
    };

    const handleTouchMove = (e) => {
        if (touchStartX === null || !isSwiping || e.touches.length !== 1) {
            return;
        }
        setTouchEndX(e.touches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStartX || !touchEndX || !isSwiping) {
            setIsSwiping(false);
            return;
        }
        const distance = touchStartX - touchEndX;
        const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
        const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;

        if (isLeftSwipe) {
            handleNextSlide();
        } else if (isRightSwipe) {
            handlePrevSlide();
        }

        setTouchStartX(null);
        setTouchEndX(null);
        setIsSwiping(false);
    };

    // --- Player Card Component for Mobile --- (This entire definition will be removed)
    // const PlayerCard = ({ player, stats, snippets, selectedDays, isExpanded, onToggleExpand, onPlaySnippet, selectedClassifications }) => {
    // ... (entire PlayerCard component definition was here) ...
    // };

    // More robust initial loading check for the whole page
    if (authLoading || (isLoadingProfile && !user && !profile)) { // Show loading if auth is loading, OR if profile is loading AND user/profile aren't set yet.
        return <div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" /></div>;
    }
    if (!user && !authLoading) { // If no user after auth has loaded
        return (
            <div className="min-h-screen flex flex-col items-center justify-center text-center p-4">
                <h2 className="text-2xl font-semibold mb-4">Please Log In</h2>
                <p className="mb-6">You need to be logged in to view your profile and teams.</p>
                <Link to="/auth" className="btn btn-primary">Go to Login</Link>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-base-content"> </h1>
                <Link to="/addteam" className="btn btn-accent btn-sm">
                    <span className="hidden sm:inline">Add Your Teams</span>
                    <PlusIcon  className="w-5 h-5 sm:hidden" />
                </Link>
            </div>

            {error && <ErrorMessage message={error} />}
            {/* {deleteTeamsSuccess && <SuccessMessage message={deleteTeamsSuccess} />} -- REMOVE */}

            {/* Main Content Area: Conditionally render Mobile Carousel or Desktop Grid */}
            {isMobile ? (
                <>
                    {/* --- MOBILE CAROUSEL --- */}
                    <div
                        ref={carouselRef}
                        className="relative overflow-hidden bg-base-200 mt-4 touch-pan-y"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        {/* Sliding Track */}
                        <div
                            className="flex transition-transform duration-300 ease-in-out"
                            style={{ transform: `translateX(-${currentMobileSlideIndex * 100}%)` }}
                        >
                            {/* Slide 1: Overall Stats (NEW ORDER) */}
                            <div className="w-full flex-shrink-0 px-1 py-2">
                                <OverallStatsCard
                                    selectedDays={selectedDays}
                                    onDaysButtonClick={handleDaysButtonClick}
                                    overallStats={overallStats}
                                    selectedClassifications={selectedClassifications}
                                    onClassificationFilterClick={handleClassificationFilterClick}
                                    onClearClassificationFilters={() => setSelectedClassifications(new Set())}
                                    isLoadingSnippetsAndStats={isLoadingSnippetsAndStats}
                                    isLoadingPlayers={isLoadingPlayers}
                                    displayedPlayersCount={displayedPlayers.length} // Pass count
                                    isMobile={true}
                                />
                            </div>

                            {/* Slide 2: Filter by Team (NEW ORDER) */}
                            <div className="w-full flex-shrink-0 px-1 py-2">
                                 <TeamFilterCard 
                                    userTeams={userTeams}
                                    isLoadingTeams={isLoadingTeams}
                                    selectedTeamIds={selectedTeamIds}
                                    isAllTeamsSelected={isAllTeamsSelected}
                                    onTeamSelectionChange={handleTeamSelectionChange}
                                    onSelectAllTeams={handleSelectAllTeams}
                                    onSelectNoneTeams={handleSelectNoneTeams}
                                    onResyncTeam={handleResyncTeam}
                                    isResyncing={isResyncing}
                                    teamToResync={teamToResync}
                                    onTeamsDeletedSuccessfully={handleTeamsDeletedSuccessfully}
                                    isMobile={true}
                                 />
                            </div>
                            
                            {/* Slide 3: My Profile (NEW ORDER) */}
                            <div className="w-full flex-shrink-0 px-1 py-2">
                                <div className="card bg-base-100 shadow-md p-4 h-60 overflow-y-auto">
                                    <UserProfileCard 
                                        profile={profile}
                                        isLoadingProfile={isLoadingProfile}
                                        isEditingProfile={isEditingProfile}
                                        onToggleEdit={handleEditProfileToggle}
                                        avatarPreview={avatarPreview}
                                        onFileSelect={handleFileSelect}
                                        editFirstName={editFirstName}
                                        onFirstNameChange={setEditFirstName} // Pass setter directly
                                        editLastName={editLastName}
                                        onLastNameChange={setEditLastName} // Pass setter directly
                                        selectedFile={selectedFile}
                                        onSubmit={handleProfileFormSubmit}
                                        isUpdatingProfile={isUpdatingProfile}
                                        profileUpdateError={profileUpdateError}
                                        profileUpdateSuccess={profileUpdateSuccess}
                                        displayInitial={displayInitial}
                                        displayName={displayName}
                                        isMobile={true} // Indicate this is the mobile version
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Navigation Chevrons */}
                        {currentMobileSlideIndex > 0 && (
                            <button
                                onClick={handlePrevSlide}
                                className="absolute top-1/2 left-1 transform -translate-y-1/2 btn btn-circle btn-ghost btn-sm text-white bg-black/40 hover:bg-black/60 z-20 focus:outline-none focus:ring-2 focus:ring-primary"
                                aria-label="Previous Slide"
                                title="Previous"
                            >
                                <ChevronLeftIcon className="w-5 h-5" />
                            </button>
                        )}
                        {currentMobileSlideIndex < 2 && ( // 3 slides (0, 1, 2)
                            <button
                                onClick={handleNextSlide}
                                className="absolute top-1/2 right-1 transform -translate-y-1/2 btn btn-circle btn-ghost btn-sm text-white bg-black/40 hover:bg-black/60 z-20 focus:outline-none focus:ring-2 focus:ring-primary"
                                aria-label="Next Slide"
                                title="Next"
                            >
                                <ChevronRightIcon className="w-5 h-5" />
                            </button>
                        )}

                        {/* Navigation Dots */}
                        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1.5 z-20">
                            {[0, 1, 2].map(index => ( // 3 slides
                                <button
                                    key={index}
                                    onClick={() => setCurrentMobileSlideIndex(index)}
                                    className={`w-2 h-2 rounded-full transition-colors duration-200 ${currentMobileSlideIndex === index ? 'bg-primary scale-110' : 'bg-base-content/40 hover:bg-base-content/60'}`}
                                    aria-label={`Go to slide ${index + 1}`}
                                    title={`Go to slide ${index + 1}`}
                                ></button>
                            ))}
                        </div>
                    </div>
                    {/* --- END MOBILE CAROUSEL --- */}

                    {/* Player List Section for Mobile (after carousel) */}
                    <div className="card bg-base-100 shadow-md p-4 mt-4">
                        <h3 className="text-md md:text-lg font-semibold mb-3">Player Details (Last {selectedDays === 0 ? 'All Time' : `${selectedDays}D`})</h3>
                        {isLoadingPlayers ? (<div className="text-center p-4"><LoadingSpinner /></div>)
                        : displayedPlayers.length === 0 ? (<p className="text-center text-base-content/70 italic py-4">{selectedTeamIds.size > 0 ? "No players for selected team(s)." : "Select a team to view players."}</p>)
                        : (
                            <div className="block md:hidden space-y-3 mt-2">
                                {isLoadingSnippetsAndStats && displayedPlayers.length > 0 ? (
                                    <div className="text-center"><LoadingSpinner size="sm" /> <span className="text-xs italic">Loading stats...</span></div>
                                ) : (
                                    [...displayedPlayers].sort((a, b) => (perPlayerStats[b.id]?.total || 0) - (perPlayerStats[a.id]?.total || 0)).map(player => {
                                        const stats = perPlayerStats[player.id] || defaultAggregatedStats;
                                        return <PlayerCard 
                                            key={player.id} 
                                            player={player} 
                                            stats={stats} 
                                            snippets={playerSnippets[player.id] || []}
                                            selectedDays={selectedDays}
                                            isExpanded={expandedPlayerId === player.id}
                                            onToggleExpand={() => toggleSnippetsExpansion(player.id)}
                                            onPlaySnippet={handlePlaySnippet}
                                            selectedClassifications={selectedClassifications}
                                            onPlayerNameClick={handlePlayerClick}
                                        />;
                                    })
                                )}
                            </div>
                        )}
                        {isLoadingSnippetsAndStats && displayedPlayers.length > 0 && !isLoadingPlayers && (<div className="text-center pt-2"><LoadingSpinner size="sm"/> <span className="text-xs italic ml-1">Updating player stats...</span></div>)}
                    </div>
                </>
            ) : (
                // --- DESKTOP GRID LAYOUT ---
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    <aside ref={asideRef} className="lg:col-span-1 space-y-6">
                        {/* User Profile Card - Desktop */}
                        {/* Use UserProfileCard component here */}
                        <UserProfileCard 
                            profile={profile}
                            isLoadingProfile={isLoadingProfile}
                            isEditingProfile={isEditingProfile}
                            onToggleEdit={handleEditProfileToggle}
                            avatarPreview={avatarPreview}
                            onFileSelect={handleFileSelect}
                            editFirstName={editFirstName}
                            onFirstNameChange={setEditFirstName} // Pass setter directly
                            editLastName={editLastName}
                            onLastNameChange={setEditLastName} // Pass setter directly
                            selectedFile={selectedFile}
                            onSubmit={handleProfileFormSubmit}
                            isUpdatingProfile={isUpdatingProfile}
                            profileUpdateError={profileUpdateError}
                            profileUpdateSuccess={profileUpdateSuccess}
                            displayInitial={displayInitial}
                            displayName={displayName}
                            isMobile={false} // Indicate this is the desktop version
                        />

                        {/* Desktop YouTube Player Placeholder */}
                        {!isMobile && selectedSnippet && (
                            <div ref={playerRef} className="mt-6">
                                <div className={`card bg-base-100 shadow-md p-0 overflow-hidden relative transition-all duration-200 ${isPlayerSticky ? 'sticky top-20 z-20' : ''}`}> 
                                    <button 
                                        onClick={handleStopPlayer} 
                                        className="btn btn-ghost btn-xs p-1 absolute top-1 right-1 z-10 bg-black/30 text-white hover:bg-black/50" 
                                        title="Close Player"
                                    >
                                        <XMarkIcon className="w-4 h-4" /> 
                                    </button>
                                    <YouTubePlayer
                                        selectedSnippet={selectedSnippet}
                                        isPlaying={isPlaying}
                                        isMobile={false}
                                        isMinimized={false}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Team Filter Card - Desktop */}
                        <div ref={filterCardRef} /* className="card bg-base-100 shadow-md p-4" // Base card styling handled by TeamFilterCard */ >
                            {/* Use TeamFilterCard component here */}
                            <TeamFilterCard 
                                userTeams={userTeams}
                                isLoadingTeams={isLoadingTeams}
                                selectedTeamIds={selectedTeamIds}
                                isAllTeamsSelected={isAllTeamsSelected}
                                onTeamSelectionChange={handleTeamSelectionChange}
                                onSelectAllTeams={handleSelectAllTeams}
                                onSelectNoneTeams={handleSelectNoneTeams}
                                onResyncTeam={handleResyncTeam}
                                isResyncing={isResyncing}
                                teamToResync={teamToResync}
                                onTeamsDeletedSuccessfully={handleTeamsDeletedSuccessfully}
                                isMobile={false}
                            />
                        </div>
                    </aside>

                    <main className="lg:col-span-3 space-y-6">
                        {/* Overall Stats Section - Desktop */}
                        {/* Use OverallStatsCard component here */}
                        <OverallStatsCard
                            selectedDays={selectedDays}
                            onDaysButtonClick={handleDaysButtonClick}
                            overallStats={overallStats}
                            selectedClassifications={selectedClassifications}
                            onClassificationFilterClick={handleClassificationFilterClick}
                            onClearClassificationFilters={() => setSelectedClassifications(new Set())}
                            isLoadingSnippetsAndStats={isLoadingSnippetsAndStats}
                            isLoadingPlayers={isLoadingPlayers}
                            displayedPlayersCount={displayedPlayers.length} // Pass count
                            isMobile={false}
                        />

                        {/* Player List Section - Desktop */}
                        <div className="card bg-base-100 shadow-md p-4">
                            <h3 className="text-md md:text-lg font-semibold mb-3">Player Details (Last {selectedDays === 0 ? 'All Time' : `${selectedDays}D`})</h3>
                            {/* Use DesktopPlayerTable component here */}
                            <DesktopPlayerTable 
                                displayedPlayers={displayedPlayers}
                                perPlayerStats={perPlayerStats}
                                playerSnippets={playerSnippets}
                                allClassifications={allClassifications}
                                expandedPlayerId={expandedPlayerId}
                                onToggleSnippetsExpansion={toggleSnippetsExpansion} // Corrected prop name
                                onPlayerNameClick={handlePlayerClick} // Pass the navigation handler
                                onPlaySnippet={handlePlaySnippet}
                                selectedDays={selectedDays}
                                selectedClassifications={selectedClassifications}
                                isLoadingPlayers={isLoadingPlayers}
                                isLoadingSnippetsAndStats={isLoadingSnippetsAndStats}
                                selectedTeamIds={selectedTeamIds} // Pass selectedTeamIds for empty state message
                            />
                        </div>
                    </main>
                </div>
            )}
            {/* End Main Content Area */}

            {/* --- Mobile Sticky Player (Moved outside main grid for positioning) --- */}
            {isMobile && selectedSnippet && (
                <div
                    id="mobile-sticky-player-container"
                    className={`fixed md:hidden top-16 left-0 z-40 w-full h-auto 
                            bg-gradient-to-b from-base-300 via-base-200 to-base-200/90 
                            shadow-lg /* Standard shadow */
                            transition-transform duration-300 ease-in-out transform flex items-stretch 
                    ${isPlayerMinimized ? `-translate-x-[calc(100%-44px)]` : 'translate-x-0'} 
                `} 
                >
                    {/* Player takes most space */} 
                    <div className="flex-grow min-w-0 overflow-hidden rounded-b-lg"> {/* Changed rounding */} 
                        <YouTubePlayer
                            selectedSnippet={selectedSnippet}
                            isPlaying={isPlaying}
                            isMobile={true}
                            isMinimized={isPlayerMinimized}
                        />
                    </div>
                    {/* Controls container (fixed width, vertical flex) */} 
                    <div className={`flex-shrink-0 w-[44px] flex flex-col items-center justify-center gap-y-3 px-1 bg-inherit rounded-br-lg shadow-inner`}> {/* Changed rounding */} 
                        {/* Minimize/Expand Button */} 
                        <button
                            onClick={togglePlayerMinimize}
                            className="p-1.5 bg-black/40 text-white rounded-full hover:bg-black/60 focus:outline-none focus:ring-1 focus:ring-white"
                            aria-label={isPlayerMinimized ? "Expand Player" : "Minimize Player"}
                            title={isPlayerMinimized ? "Expand Player" : "Minimize Player"}
                        >
                            {isPlayerMinimized ? (
                                <ChevronRightIcon className="w-5 h-5" /> // Icon to expand right
                            ) : (
                                <ChevronLeftIcon className="w-5 h-5" /> // Icon to minimize left
                            )}
                        </button>
                        {/* Close Button - Show only when Expanded */} 
                        {!isPlayerMinimized && (
                            <button
                                onClick={handleStopPlayer}
                                className="p-1.5 bg-black/40 text-white rounded-full hover:bg-black/60 focus:outline-none focus:ring-1 focus:ring-white"
                                aria-label="Close Player"
                                title="Close Player"
                            >
                                <XMarkIcon className="w-5 h-5" /> 
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default UserPage;